import { useState, useRef, useEffect } from "react"
import "./SeccionPropuestas.css"
import { collection, addDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../../services/firebase"
import { useAuth } from "../../context/AuthContext"
import { validarCotizacion, guardarCotizacion } from "../../services/cotizacionService"
import {
  CIUDADES_SALIDA, AEROLINEAS, INCLUYE_OPTS, REGALO_OPTS,
  HOTELES, DESTINOS, DESTINO_IMGS, CAT_COLORS, CAT_BG, HOY_STR, FORM_VACIO,
  formatFecha, getIncluye, getRegalo, getEquipaje, getWAText
} from "./propuestasData"

// ═══════════════════════════════════════════════════════════
// HOTELES SELECTOR
// ═══════════════════════════════════════════════════════════
// Sub-componente reutilizable para cada fila de hotel
function HotelItem({ keyName, nombre, desc, sel, precio, tachado, onChange, extraField }) {
  return (
    <div>
      <div
        className={`gen-hotel-item${sel?" gen-hotel-item--activo":""}`}
        onClick={()=>onChange(keyName,"sel",!sel)}
      >
        <div className={`gen-hotel-check${sel?" gen-hotel-check--activo":""}`}>
          {sel && <span className="gen-hotel-check__tick">✓</span>}
        </div>
        <div style={{flex:1}}>
          <div className="gen-hotel-nombre">{nombre}</div>
          {desc && <div className="gen-hotel-desc">{desc}</div>}
        </div>
      </div>
      {sel && extraField}
      {sel && (
        <div className="gen-precio-row">
          <div>
            <label>Precio USD</label>
            <input type="number" placeholder="0" value={precio}
              className="gen-precio-input"
              onClick={e=>e.stopPropagation()}
              onChange={e=>onChange(keyName,"precio",e.target.value)} />
          </div>
          <div>
            <label>Tachado (promo)</label>
            <input type="number" placeholder="—" value={tachado}
              className="gen-precio-tachado"
              onClick={e=>e.stopPropagation()}
              onChange={e=>onChange(keyName,"tachado",e.target.value)} />
          </div>
        </div>
      )}
    </div>
  )
}

function HotelesSelector({ destino, hoteles, onChange }) {
  const [abiertos, setAbiertos] = useState({ "Económico":false, "Intermedio":false, "Exclusivo":false })
  if (!destino) return null
  const tieneHoteles = !!HOTELES[destino]
  const cats = ["Económico","Intermedio","Exclusivo"]
  const toggle = cat => setAbiertos(p=>({...p,[cat]:!p[cat]}))

  // Slots libres dinámicos: aparece el siguiente cuando el anterior está seleccionado
  const libreKeys = []
  for (let i = 0; i < 5; i++) {
    const key = i === 0 ? "__libre__" : `__libre${i+1}__`
    libreKeys.push(key)
    if (!hoteles[key]?.sel) break
  }

  return (
    <div style={{ marginTop:8 }}>

      {/* ── Opciones globales (sin tier) ── */}
      <div className="gen-global-opts">
        <span className="gen-global-opts__label">Sin categoría</span>
        <HotelItem
          keyName="__aeleccion__"
          nombre="A elección"
          desc=""
          sel={hoteles["__aeleccion__"]?.sel||false}
          precio={hoteles["__aeleccion__"]?.precio||""}
          tachado={hoteles["__aeleccion__"]?.tachado||""}
          onChange={onChange}
        />
        {libreKeys.map(key=>(
          <HotelItem
            key={key}
            keyName={key}
            nombre={hoteles[key]?.nombre?.trim() || "✏️ Escribir nombre..."}
            desc=""
            sel={hoteles[key]?.sel||false}
            precio={hoteles[key]?.precio||""}
            tachado={hoteles[key]?.tachado||""}
            onChange={onChange}
            extraField={
              <div style={{ padding:"4px 8px 6px 24px" }}>
                <input
                  type="text"
                  placeholder="Nombre del hotel o posada"
                  value={hoteles[key]?.nombre||""}
                  className="gen-input"
                  style={{ marginTop:4, fontSize:13 }}
                  onClick={e=>e.stopPropagation()}
                  onChange={e=>onChange(key,"nombre",e.target.value)}
                />
              </div>
            }
          />
        ))}
      </div>

      {/* ── Tiers como acordeones (solo si el destino tiene hoteles predefinidos) ── */}
      {tieneHoteles && cats.map(cat => {
        const lista = HOTELES[destino].filter(h=>h.cat===cat)
        if (!lista.length) return null
        return (
          <div key={cat}>
            <div className="gen-tier-header" onClick={()=>toggle(cat)}>
              <span className="gen-tier-header__label" style={{ color:CAT_COLORS[cat] }}>{cat}</span>
              <span className="gen-tier-header__arrow">{abiertos[cat] ? "▲" : "▼"}</span>
            </div>
            {abiertos[cat] && (
              <div className="gen-tier-body">
                {lista.map(h=>(
                  <HotelItem
                    key={h.n}
                    keyName={h.n}
                    nombre={h.n}
                    desc={`${h.u} · ${h.d}`}
                    sel={hoteles[h.n]?.sel||false}
                    precio={hoteles[h.n]?.precio||""}
                    tachado={hoteles[h.n]?.tachado||""}
                    onChange={onChange}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// PROPUESTA CARD (preview descargable)
// ═══════════════════════════════════════════════════════════
function PropuestaCard({ form, cardRef, mostrarImgs }) {
  const aero   = AEROLINEAS.find(a=>a.key===form.aerolinea)
  const ciudad = form.ciudadSalida==="Otros" ? form.ciudadOtra : form.ciudadSalida
  const inc    = getIncluye(form.incluye)
  const eq     = getEquipaje(form.equipaje)
  const reg    = getRegalo(form.regalo, form.cenaCantidad, form.regaloLibreTexto)
  const hayDatos = form.cliente || form.destino1 || form.aerolinea || form.fechaSalida
  const dest1Display = form.destino1==="Libre" ? (form.destino1Libre?.trim()||"Destino libre") : form.destino1
  const dest2Display = form.destino2==="Libre" ? (form.destino2Libre?.trim()||"Destino libre") : form.destino2

  const renderDestCard = (destino, destinoDisplay, noches, hoteles) => {
    if (!destino) return null
    const sel = Object.entries(hoteles)
      .filter(([,v])=>v.sel)
      .sort(([,a],[,b])=>(parseFloat(a.precio)||0)-(parseFloat(b.precio)||0))
    const hayPrecios = sel.some(([,v])=>v.precio)
    return (
      <div key={destino} style={{ marginBottom:16 }}>
        <div className="gen-prop-card__dest-titulo">
          {destinoDisplay}
          <span className="gen-prop-card__noches-badge">{noches} noches</span>
        </div>
        {hayPrecios && (
          <div className="gen-prop-card__por-persona">precios por persona</div>
        )}
        {sel.length===0 && <div className="gen-prop-card__sin-hoteles">Sin hoteles seleccionados</div>}
        {sel.map(([nombre, val]) => {
          // ── Global: A elección (sin tier) ──
          if (nombre === "__aeleccion__") {
            return (
              <div key={nombre} className="gen-prop-card__hotel-row">
                <div className="gen-prop-card__hotel-left">
                  <div className="gen-prop-card__hotel-nombre">A elección</div>
                </div>
                {val.precio && (
                  <div className="gen-prop-card__hotel-right">
                    {val.tachado && <span className="gen-prop-card__precio-tachado">USD {val.tachado}</span>}
                    <span className="gen-prop-card__precio">USD {val.precio}</span>
                  </div>
                )}
              </div>
            )
          }
          // ── Global: Hotel libre (sin tier) ──
          if (nombre === "__libre__" || /^__libre\d+__$/.test(nombre)) {
            const displayName = val.nombre?.trim() || "Hotel a definir"
            return (
              <div key={nombre} className="gen-prop-card__hotel-row">
                <div className="gen-prop-card__hotel-left">
                  <div className="gen-prop-card__hotel-nombre">{displayName}</div>
                </div>
                {val.precio && (
                  <div className="gen-prop-card__hotel-right">
                    {val.tachado && <span className="gen-prop-card__precio-tachado">USD {val.tachado}</span>}
                    <span className="gen-prop-card__precio">USD {val.precio}</span>
                  </div>
                )}
              </div>
            )
          }
          // ── Legacy: A elección con tier ──
          if (nombre.startsWith("__aeleccion__")) {
            const cat = nombre.replace("__aeleccion__","")
            const catStyle = CAT_BG[cat] || {}
            return (
              <div key={nombre} className="gen-prop-card__hotel-row">
                <div className="gen-prop-card__hotel-left">
                  <span className="gen-prop-card__hotel-cat" style={{ background:catStyle.bg, color:catStyle.color }}>{cat}</span>
                  <div className="gen-prop-card__hotel-nombre">A elección</div>
                </div>
                {val.precio && (
                  <div className="gen-prop-card__hotel-right">
                    {val.tachado && <span className="gen-prop-card__precio-tachado">USD {val.tachado}</span>}
                    <span className="gen-prop-card__precio">USD {val.precio}</span>
                  </div>
                )}
              </div>
            )
          }
          // ── Legacy: Hotel libre con tier ──
          if (nombre.startsWith("__libre__")) {
            const cat = nombre.replace("__libre__","")
            const catStyle = CAT_BG[cat] || {}
            const displayName = val.nombre?.trim() || "Hotel a definir"
            return (
              <div key={nombre} className="gen-prop-card__hotel-row">
                <div className="gen-prop-card__hotel-left">
                  <span className="gen-prop-card__hotel-cat" style={{ background:catStyle.bg, color:catStyle.color }}>{cat}</span>
                  <div className="gen-prop-card__hotel-nombre">{displayName}</div>
                </div>
                {val.precio && (
                  <div className="gen-prop-card__hotel-right">
                    {val.tachado && <span className="gen-prop-card__precio-tachado">USD {val.tachado}</span>}
                    <span className="gen-prop-card__precio">USD {val.precio}</span>
                  </div>
                )}
              </div>
            )
          }
          // ── Hotel predefinido ──
          const hotel = HOTELES[destino]?.find(h=>h.n===nombre)
          if (!hotel) return null
          const catStyle = CAT_BG[hotel.cat] || {}
          return (
            <div key={nombre} className="gen-prop-card__hotel-row">
              <div className="gen-prop-card__hotel-left">
                <span className="gen-prop-card__hotel-cat" style={{ background:catStyle.bg, color:catStyle.color }}>
                  {hotel.cat}
                </span>
                <div className="gen-prop-card__hotel-nombre">{hotel.n}</div>
                <div className="gen-prop-card__hotel-desc">{hotel.u} · {hotel.d}</div>
              </div>
              {val.precio && (
                <div className="gen-prop-card__hotel-right">
                  {val.tachado && <span className="gen-prop-card__precio-tachado">USD {val.tachado}</span>}
                  <span className="gen-prop-card__precio">USD {val.precio}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div ref={cardRef} className="gen-prop-card">
      {!hayDatos ? (
        <div className="gen-prop-card__empty">
          <div className="gen-prop-card__empty-icon">✈️</div>
          <div className="gen-prop-card__empty-text">Completá el formulario para ver el preview</div>
        </div>
      ) : (
        <>
          <div className="gen-prop-card__header">
            <img src="/logos/logo-lowtravel.png" alt="Low Travel" className="gen-prop-card__logo" />
            {aero && (
              <div className="gen-prop-card__aero-wrap">
                <img src={aero.logo} alt={aero.label} className="gen-prop-card__aero" />
              </div>
            )}
          </div>

          {mostrarImgs && form.destino1 && DESTINO_IMGS[form.destino1] && (
            <div style={{ margin:"0 -36px 16px", height:140, overflow:"hidden", position:"relative" }}>
              <img
                src={DESTINO_IMGS[form.destino1]}
                alt={form.destino1}
                crossOrigin="anonymous"
                style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
                onError={e=>{ e.currentTarget.parentElement.style.display="none" }}
              />
              {form.destino2 && DESTINO_IMGS[form.destino2] && (
                <div style={{ position:"absolute", bottom:0, right:0, width:"40%", height:"100%" }}>
                  <img
                    src={DESTINO_IMGS[form.destino2]}
                    alt={form.destino2}
                    crossOrigin="anonymous"
                    style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", borderLeft:"2px solid #fdf6ec" }}
                    onError={e=>{ e.currentTarget.parentElement.style.display="none" }}
                  />
                </div>
              )}
              <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom, transparent 40%, rgba(253,246,236,0.6) 100%)" }}/>
            </div>
          )}

          <div className="gen-prop-card__titulo">
            {dest1Display
              ? `Viaje a ${dest1Display}${dest2Display?" + "+dest2Display:""}`
              : <span className="gen-prop-card__titulo--placeholder">Destino por definir</span>
            }
          </div>
          {form.cliente && <div className="prop-card__para">Para: <strong>{form.cliente}</strong></div>}
          {aero  && <div className="gen-prop-card__info-row">✈️ {aero.label}</div>}
          {eq    && <div className="gen-prop-card__info-row">🧳 {eq}</div>}
          {ciudad&& <div className="gen-prop-card__info-row">🏙️ Desde {ciudad}</div>}

          {(form.fechaSalida || form.fechaRegreso) && (
            <div className="gen-prop-card__fechas">
              {form.fechaSalida && (
                <div>
                  <div className="gen-prop-card__fecha-label">Salida</div>
                  <div className="gen-prop-card__fecha-val">{formatFecha(form.fechaSalida)}</div>
                </div>
              )}
              {form.fechaRegreso && (
                <div>
                  <div className="gen-prop-card__fecha-label">Regreso</div>
                  <div className="gen-prop-card__fecha-val">{formatFecha(form.fechaRegreso)}</div>
                </div>
              )}
            </div>
          )}

          {form.destino1 && (
            <>
              <hr className="gen-prop-card__divider" />
              {renderDestCard(form.destino1, dest1Display, form.noches1, form.hoteles1)}
              {form.destino2 && renderDestCard(form.destino2, dest2Display, form.noches2, form.hoteles2)}
            </>
          )}

          {inc.length > 0 && (
            <div className="gen-prop-card__incluye">
              <div className="gen-prop-card__incluye-titulo">El paquete incluye</div>
              <div className="gen-prop-card__incluye-chips">
                {inc.map(i=><span key={i} className="gen-prop-card__chip">{i}</span>)}
              </div>
            </div>
          )}

          {reg.length > 0 && (
            <div className="gen-prop-card__regalo">
              <div className="gen-prop-card__regalo-titulo">🎁 Regalo incluido</div>
              <div className="gen-prop-card__incluye-chips">
                {reg.map(r=><span key={r} className="gen-prop-card__regalo-chip">{r}</span>)}
              </div>
            </div>
          )}

          {(() => {
            const p = form.precio || {}
            const BLOQUE = { margin:"14px 0 10px", padding:"14px 16px", background:"#f0fdf4", border:"1.5px solid #16a34a", borderRadius:"10px" }
            const TITULO = { fontSize:"14px", fontWeight:800, textTransform:"uppercase", letterSpacing:"1px", color:"#d38328", marginBottom:8, textAlign:"center" }
            const MONTO  = { fontSize:"1.2rem", fontWeight:900, color:"#16a34a", letterSpacing:"-0.02em" }
            const LABEL  = { fontSize:"15px", fontWeight:700, color:"#374151", marginBottom:4 }

            if (p.modo === "total" && p.total) return (
              <div style={{ ...BLOQUE, textAlign:"center" }}>
                <div style={TITULO}>Precio por persona</div>
                <div style={MONTO}>USD {Number(p.total).toLocaleString("es-AR")}</div>
              </div>
            )
            if (p.modo === "cuotas" && p.reserva && p.cuotas) {
              const n = s => Number(String(s).replace(/,/g, ""))
              const paxNum    = n(p.pax) || 1
              const reservaPP = Math.round(n(p.reserva) / paxNum)
              const cuotaMonto = (p.total && p.reserva && p.cuotas)
                ? Math.ceil((n(p.total) - reservaPP) / n(p.cuotas))
                : null
              return (
                <div style={BLOQUE}>
                  <div style={TITULO}>Plan de pago · por persona</div>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:12, flexWrap:"wrap" }}>
                    <div style={{ textAlign:"center" }}>
                      <div style={LABEL}>Reservá con</div>
                      <div style={MONTO}>USD {reservaPP.toLocaleString("es-AR")}</div>
                    </div>
                    <div style={{ color:"#16a34a", fontSize:"1.6rem", fontWeight:700, lineHeight:1, marginTop:18 }}>+</div>
                    <div style={{ textAlign:"center" }}>
                      <div style={LABEL}>{p.cuotas} cuotas de</div>
                      <div style={MONTO}>
                        {cuotaMonto ? `USD ${cuotaMonto.toLocaleString("es-AR")}` : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
            if (p.modo === "rango" && p.rangoMin && p.rangoMax) return (
              <div style={{ ...BLOQUE, textAlign:"center" }}>
                <div style={TITULO}>Precio por persona</div>
                <div style={{ ...MONTO, fontSize:"1.2rem" }}>
                  Entre {Number(p.rangoMin)} y {Number(p.rangoMax)} USD
                </div>
              </div>
            )
            return null
          })()}
          <div className="gen-prop-card__footer-msg">El precio de la cotización se mantiene por 48 hs</div>
          <div className="gen-prop-card__fecha-cot">Cotización: {HOY_STR}</div>
        </>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════
export default function SeccionPropuestas({ initialForm, cotizacionContext }) {
  const { user } = useAuth()
  const [form,        setForm]        = useState(() =>
    initialForm
      ? { ...FORM_VACIO, ...initialForm,
          incluye:  { ...FORM_VACIO.incluye,  ...(initialForm.incluye  || {}) },
          regalo:   { ...FORM_VACIO.regalo,   ...(initialForm.regalo   || {}) },
          equipaje: { ...FORM_VACIO.equipaje, ...(initialForm.equipaje || {}) },
          precio:   { ...FORM_VACIO.precio,   ...(initialForm.precio   || {}) },
        }
      : FORM_VACIO
  )
  const [mostrarImgs, setMostrarImgs] = useState(false)
  const [waText,      setWaText]      = useState("")
  const [copiado,     setCopiado]     = useState(false)
  const [descargando, setDescargando] = useState(false)
  const [shake,       setShake]       = useState(false)
  const [saveState,   setSaveState]   = useState("idle")   // idle | saving | saved | error
  const [saveError,   setSaveError]   = useState(null)
  const [saveErrList, setSaveErrList] = useState([])
  const [cotizacionId,setCotizacionId]= useState(null)     // evita doble guardado
  const cardRef = useRef(null)

  const set       = (k,v) => setForm(p=>({...p,[k]:v}))
  const setHotel  = (dest,nombre,campo,valor) => {
    const key = dest===1?"hoteles1":"hoteles2"
    setForm(p=>({...p,[key]:{...p[key],[nombre]:{...(p[key][nombre]||{}),[campo]:valor}}}))
  }
  const setIncluye = (k,v) => setForm(p=>({...p,incluye:{...p.incluye,[k]:v}}))
  const setRegalo  = (k,v) => setForm(p=>({...p,regalo:{...p.regalo,[k]:v}}))
  const setEquipaje= (k,v) => setForm(p=>({...p,equipaje:{...p.equipaje,[k]:v}}))
  const setPrecio  = (k,v) => setForm(p=>({...p,precio:{...p.precio,[k]:v}}))

  useEffect(()=>{ setWaText(getWAText(form)) },[form])

  async function handleGuardar() {
    if (saveState === "saving" || cotizacionId) return

    if (!cotizacionContext) {
      setSaveErrList(["Esta propuesta no fue generada desde el Cotizador."])
      setSaveState("error")
      return
    }

    const errores = validarCotizacion(cotizacionContext)
    if (errores.length > 0) {
      setSaveErrList(errores)
      setSaveState("idle")
      return
    }

    setSaveState("saving")
    setSaveError(null)
    setSaveErrList([])

    try {
      const docData = {
        ...cotizacionContext,
        propuestaFinal: {
          cliente:          form.cliente,
          ciudadSalida:     form.ciudadSalida,
          ciudadOtra:       form.ciudadOtra,
          aerolinea:        form.aerolinea,
          equipaje:         form.equipaje,
          destino1:         form.destino1,
          destino1Libre:    form.destino1Libre,
          noches1:          form.noches1,
          destino2:         form.destino2,
          destino2Libre:    form.destino2Libre,
          noches2:          form.noches2,
          hoteles1:         form.hoteles1,
          hoteles2:         form.hoteles2,
          incluye:          form.incluye,
          regalo:           form.regalo,
          cenaCantidad:     form.cenaCantidad,
          regaloLibreTexto: form.regaloLibreTexto,
          precio:           form.precio,
        },
      }
      const id = await guardarCotizacion(docData)
      setCotizacionId(id)
      setSaveState("saved")
    } catch (e) {
      console.error("Error al guardar cotización:", e)
      setSaveError("Error al guardar. Revisá la conexión e intentá de nuevo.")
      setSaveState("error")
    }
  }

  const descargarImagen = async () => {
    if (!form.destino1) {
      setShake(true); setTimeout(()=>setShake(false),600); return
    }
    setDescargando(true)
    try {
      const html2canvas = (await import("html2canvas")).default
      const canvas = await html2canvas(cardRef.current,{scale:2,useCORS:true,backgroundColor:"#fdf6ec"})
      const link = document.createElement("a")
      link.download = `propuesta-${form.cliente||"lowtravel"}-${form.destino1||"destino"}.png`
      link.href = canvas.toDataURL("image/png")
      link.click()
    } catch(e){ console.error(e) }
    setDescargando(false)
  }

  const copiarWA = () => {
    navigator.clipboard.writeText(waText)
    setCopiado(true); setTimeout(()=>setCopiado(false),2500)
  }

  return (
    <div>
      <div className="admin__seccion-header">
        <div>
          <div className="admin__seccion-titulo">Generador de Propuestas</div>
          <div className="admin__seccion-sub">Armá la cotización y descargá la imagen o copiá el texto para WhatsApp</div>
        </div>
      </div>

      {/* ── DATOS DEL VIAJE — fila completa ── */}
      <div className="gen-datos-wrap">
        <div className="gen-card">
          <div className="gen-card__titulo">Datos del viaje</div>
          <div className="gen-two-col">
            <div>
              <label className="gen-label">Cliente</label>
              <input className="gen-input" type="text" placeholder="Nombre del cliente"
                value={form.cliente} onChange={e=>set("cliente",e.target.value)} />
            </div>
            <div>
              <label className="gen-label">Aerolínea</label>
              <select className="gen-input" value={form.aerolinea} onChange={e=>set("aerolinea",e.target.value)}>
                <option value="">Ninguna</option>
                {AEROLINEAS.map(a=><option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </div>
          </div>
          <div className="gen-two-col">
            <div>
              <label className="gen-label">Fecha de salida</label>
              <input type="date" className="gen-input" value={form.fechaSalida} onChange={e=>set("fechaSalida",e.target.value)} />
            </div>
            <div>
              <label className="gen-label">Fecha de regreso</label>
              <input type="date" className="gen-input" value={form.fechaRegreso} onChange={e=>set("fechaRegreso",e.target.value)} />
            </div>
          </div>
          <div className="gen-two-col">
            <div>
              <label className="gen-label">Ciudad de salida</label>
              <select className="gen-input" value={form.ciudadSalida} onChange={e=>set("ciudadSalida",e.target.value)}>
                {CIUDADES_SALIDA.map(c=><option key={c}>{c}</option>)}
              </select>
              {form.ciudadSalida==="Otros" && (
                <input className="gen-input" style={{marginTop:6}} placeholder="Especificar ciudad..."
                  value={form.ciudadOtra} onChange={e=>set("ciudadOtra",e.target.value)} />
              )}
            </div>
            <div>
              <label className="gen-label">Equipaje</label>
              <div className="gen-chips">
                {[["mochila","Mochila"],["carryOn","Carry On"]].map(([k,l])=>(
                  <div key={k} className={`gen-chip${form.equipaje[k]?" gen-chip--activo":""}`}
                    onClick={()=>setEquipaje(k,!form.equipaje[k])}>{l}</div>
                ))}
                <div className={`gen-chip${form.equipaje.valija?" gen-chip--activo":""}`}
                  onClick={()=>setEquipaje("valija",!form.equipaje.valija)}>
                  Valija 23kg
                </div>
                {form.equipaje.valija && (
                  <input type="number" min="1" max="9" className="gen-valija-num"
                    value={form.equipaje.cantValijas}
                    onClick={e=>e.stopPropagation()}
                    onChange={e=>setEquipaje("cantValijas",Math.max(1,parseInt(e.target.value)||1))} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="gen-layout">

        {/* ── IZQUIERDA: destinos + extras ── */}
        <div className="gen-left-content">

          {/* Destinos — lado a lado */}
          <div className="gen-destinos-row">

            {/* Destino 1 */}
            <div className="gen-card">
              <div className="gen-card__titulo">Destino 1</div>
              <div className="gen-field">
                <label className="gen-label">Destino</label>
                <select className="gen-input" value={form.destino1}
                  onChange={e=>{ set("destino1",e.target.value); set("hoteles1",{}); set("destino1Libre","") }}>
                  <option value="">Ninguno</option>
                  {DESTINOS.map(d=><option key={d}>{d}</option>)}
                  <option value="Libre">✏️ Destino libre...</option>
                </select>
                {form.destino1==="Libre" && (
                  <input className="gen-input" style={{marginTop:6}} placeholder="Nombre del destino..."
                    value={form.destino1Libre} onChange={e=>set("destino1Libre",e.target.value)} />
                )}
              </div>
              <div className="gen-field">
                <label className="gen-label">Noches</label>
                <input type="number" min="1" max="30" className="gen-input"
                  value={form.noches1} onChange={e=>set("noches1",e.target.value)} />
              </div>
              {form.destino1 && (
                <HotelesSelector destino={form.destino1} hoteles={form.hoteles1}
                  onChange={(n,c,v)=>setHotel(1,n,c,v)} />
              )}
            </div>

            {/* Destino 2 */}
            <div className="gen-card">
              <div className="gen-card__titulo">
                Destino 2 <span style={{fontWeight:400,fontSize:11,opacity:0.5}}>(opcional)</span>
              </div>
              <div className="gen-field">
                <label className="gen-label">Destino</label>
                <select className="gen-input" value={form.destino2}
                  onChange={e=>{ set("destino2",e.target.value); set("hoteles2",{}); set("destino2Libre","") }}>
                  <option value="">Ninguno</option>
                  {DESTINOS.filter(d=>d!==form.destino1).map(d=><option key={d}>{d}</option>)}
                  <option value="Libre">✏️ Destino libre...</option>
                </select>
                {form.destino2==="Libre" && (
                  <input className="gen-input" style={{marginTop:6}} placeholder="Nombre del destino..."
                    value={form.destino2Libre} onChange={e=>set("destino2Libre",e.target.value)} />
                )}
              </div>
              {form.destino2 && (
                <div className="gen-field">
                  <label className="gen-label">Noches</label>
                  <input type="number" min="1" max="30" className="gen-input"
                    value={form.noches2} onChange={e=>set("noches2",e.target.value)} />
                </div>
              )}
              {form.destino2 && (
                <HotelesSelector destino={form.destino2} hoteles={form.hoteles2}
                  onChange={(n,c,v)=>setHotel(2,n,c,v)} />
              )}
            </div>

          </div>

          {/* Incluye + Regalo — lado a lado */}
          <div className="gen-extras-row">

            <div className="gen-card">
              <div className="gen-card__titulo">El paquete incluye</div>
              <div className="gen-grid-2">
                {INCLUYE_OPTS.map(o=>(
                  <div key={o.key}
                    className={`gen-chip-full${form.incluye[o.key]?" gen-chip-full--activo":""}`}
                    onClick={()=>setIncluye(o.key,!form.incluye[o.key])}>
                    {o.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="gen-card">
              <div className="gen-card__titulo">🎁 Regalo</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {REGALO_OPTS.map(o=>(
                  <div key={o.key} className="gen-regalo-item">
                    <div className={`gen-chip-full${form.regalo[o.key]?" gen-chip-full--activo":""}`}
                      onClick={()=>setRegalo(o.key,!form.regalo[o.key])}>
                      {o.label}
                    </div>
                    {o.key==="cena" && form.regalo.cena && (
                      <div className="gen-regalo-personas">
                        <label>Cantidad de personas:</label>
                        <input type="number" min="1" max="20" className="gen-input-num"
                          value={form.cenaCantidad}
                          onClick={e=>e.stopPropagation()}
                          onChange={e=>setForm(p=>({...p,cenaCantidad:Math.max(1,parseInt(e.target.value)||1).toString()}))} />
                      </div>
                    )}
                    {o.key==="libre" && form.regalo.libre && (
                      <div className="gen-regalo-personas" style={{ flexDirection:"column", alignItems:"flex-start" }}>
                        <label>Describí el regalo:</label>
                        <input type="text" className="gen-input"
                          style={{ marginTop:4, fontSize:13 }}
                          placeholder="Ej: Visita guiada, Spa, Tour privado..."
                          value={form.regaloLibreTexto}
                          onClick={e=>e.stopPropagation()}
                          onChange={e=>setForm(p=>({...p,regaloLibreTexto:e.target.value}))} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Plan de pago */}
          <div className="gen-card">
            <div className="gen-card__titulo">💰 Plan de pago</div>
            <div className="gen-field">
              <label className="gen-label">Modo de visualización en la propuesta</label>
              <select className="gen-input" value={form.precio.modo} onChange={e=>setPrecio("modo",e.target.value)}>
                <option value="oculto">Oculto (sin precio)</option>
                <option value="total">Total por persona</option>
                <option value="cuotas">Reserva + cuotas</option>
                <option value="rango">Rango de precios</option>
              </select>
            </div>

            {form.precio.modo === "total" && (
              <div className="gen-field">
                <label className="gen-label">Total por persona (USD)</label>
                <input type="number" min="0" className="gen-input" placeholder="Ej: 950"
                  value={form.precio.total} onChange={e=>setPrecio("total",e.target.value)} />
              </div>
            )}

            {form.precio.modo === "cuotas" && (
              <>
                <div className="gen-two-col" style={{marginTop:8}}>
                  <div>
                    <label className="gen-label">Total por persona (USD)</label>
                    <input type="number" min="0" className="gen-input" placeholder="Ej: 950"
                      value={form.precio.total} onChange={e=>setPrecio("total",e.target.value)} />
                  </div>
                  <div>
                    <label className="gen-label">Monto de reserva (USD)</label>
                    <input type="number" min="0" className="gen-input" placeholder="Ej: 200"
                      value={form.precio.reserva} onChange={e=>setPrecio("reserva",e.target.value)} />
                  </div>
                </div>
                <div className="gen-two-col" style={{marginTop:8}}>
                  <div>
                    <label className="gen-label">Cantidad de pax</label>
                    <input type="number" min="1" max="50" className="gen-input" placeholder="Ej: 2"
                      value={form.precio.pax} onChange={e=>setPrecio("pax",e.target.value)} />
                  </div>
                  <div>
                    <label className="gen-label">Cantidad de cuotas</label>
                    <input type="number" min="1" max="36" className="gen-input" placeholder="Ej: 6"
                      value={form.precio.cuotas} onChange={e=>setPrecio("cuotas",e.target.value)} />
                  </div>
                </div>
                {(() => {
                  const n  = s => Number(String(s).replace(/,/g, ""))
                  const t  = n(form.precio.total)
                  const r  = n(form.precio.reserva)
                  const px = n(form.precio.pax)
                  const c  = n(form.precio.cuotas)
                  const listo = t && r && px > 0 && c
                  const rPP   = listo ? Math.round(r / px) : null
                  const cuota = listo ? Math.ceil((t - r / px) / c) : null
                  return (
                    <div className="gen-two-col" style={{marginTop:8}}>
                      <div>
                        <label className="gen-label">Reserva por persona</label>
                        <div className="gen-input" style={{ background:"#111", fontWeight:700, color: listo ? "#c9a84c" : "#4b5563" }}>
                          {rPP ? `USD ${rPP.toLocaleString("es-AR")}` : "—"}
                        </div>
                      </div>
                      <div>
                        <label className="gen-label">Cuota por persona</label>
                        <div className="gen-input" style={{ background:"#111", fontWeight:700, color: listo ? "#c9a84c" : "#4b5563" }}>
                          {cuota ? `USD ${cuota.toLocaleString("es-AR")}` : "—"}
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </>
            )}

            {form.precio.modo === "rango" && (
              <div className="gen-two-col" style={{marginTop:8}}>
                <div>
                  <label className="gen-label">Desde (USD)</label>
                  <input type="number" min="0" className="gen-input" placeholder="Ej: 1000"
                    value={form.precio.rangoMin} onChange={e=>setPrecio("rangoMin",e.target.value)} />
                </div>
                <div>
                  <label className="gen-label">Hasta (USD)</label>
                  <input type="number" min="0" className="gen-input" placeholder="Ej: 1100"
                    value={form.precio.rangoMax} onChange={e=>setPrecio("rangoMax",e.target.value)} />
                </div>
              </div>
            )}
          </div>

        </div>

        {/* ── PREVIEW — columna derecha sticky ── */}
        <div className="gen-preview-panel">
          <div className="gen-preview-label">Preview en tiempo real</div>
          <PropuestaCard form={form} cardRef={cardRef} mostrarImgs={mostrarImgs} />

          {/* Acciones debajo de la imagen */}
          <div className="gen-acciones">
            <div
              onClick={()=>setMostrarImgs(p=>!p)}
              style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:"#1c2333", border:`1.5px solid ${mostrarImgs?"#c9a84c":"#3a4a5c"}`, borderRadius:"10px", cursor:"pointer" }}
            >
              <span style={{ fontSize:13, fontWeight:600, color:mostrarImgs?"#c9a84c":"#9aacbc" }}>
                🖼️ Mostrar fotos de destinos
              </span>
              <div style={{ width:36, height:20, borderRadius:10, background:mostrarImgs?"#c9a84c":"#3a4a5c", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                <div style={{ position:"absolute", top:3, left:mostrarImgs?18:3, width:14, height:14, borderRadius:"50%", background:"white", transition:"left 0.2s" }}/>
              </div>
            </div>
            <button
              className={`gen-btn-descarga${shake?" gen-btn-descarga--shake":""}`}
              onClick={descargarImagen} disabled={descargando}>
              {descargando ? "Generando..." : "⬇️ Descargar imagen"}
            </button>
            <button className={`gen-btn-wa${copiado?" gen-btn-wa--copiado":""}`} onClick={copiarWA}>
              {copiado ? "✅ ¡Copiado!" : "📋 Copiar texto WhatsApp"}
            </button>
            <button className="gen-btn-limpiar" onClick={()=>setForm(FORM_VACIO)}>
              🗑️ Limpiar formulario
            </button>

            {/* ── Guardar cotización ── */}
            <div style={{ marginTop:4 }}>
              {saveErrList.length > 0 && (
                <div style={{ padding:"10px 14px", background:"#1a0a0a", border:"1px solid #ef444440", borderRadius:8, marginBottom:8 }}>
                  <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#ef4444", marginBottom:5 }}>
                    No se puede guardar:
                  </div>
                  {saveErrList.map((e, i) => (
                    <div key={i} style={{ fontSize:"0.72rem", color:"#fca5a5", lineHeight:1.6 }}>• {e}</div>
                  ))}
                </div>
              )}

              <button
                onClick={handleGuardar}
                disabled={saveState === "saving" || saveState === "saved"}
                style={{
                  width:"100%", padding:"11px", borderRadius:8, fontFamily:"Inter, sans-serif",
                  fontWeight:700, fontSize:"0.875rem", cursor: saveState === "saving" || saveState === "saved" ? "not-allowed" : "pointer",
                  border:`1px solid ${saveState === "saved" ? "#10b98140" : saveState === "error" ? "#ef444440" : !cotizacionContext ? "#2a2a2a" : "#10b98130"}`,
                  background: saveState === "saved" ? "#0a200a" : saveState === "error" ? "#2a0a0a" : !cotizacionContext ? "#111" : "#0f1f0f",
                  color:      saveState === "saved" ? "#10b981" : saveState === "error" ? "#f87171" : !cotizacionContext ? "#3a3a3a" : "#6ee7b7",
                  opacity:    saveState === "saving" ? 0.7 : 1,
                  transition: "all 0.2s",
                }}>
                {saveState === "saving" ? "Guardando..." :
                 saveState === "saved"  ? "✅ Cotización guardada" :
                 saveState === "error"  ? "⚠️ Error — hacer click para reintentar" :
                 "💾 Guardar cotización"}
              </button>

              {saveState === "saved" && cotizacionId && (
                <div style={{ fontSize:"0.65rem", color:"#4b5563", textAlign:"center", marginTop:5 }}>
                  ID {cotizacionId}
                </div>
              )}
              {saveState === "error" && saveError && (
                <div style={{ fontSize:"0.68rem", color:"#f87171", textAlign:"center", marginTop:5 }}>
                  {saveError}
                </div>
              )}
              {!cotizacionContext && (
                <div style={{ fontSize:"0.65rem", color:"#3a3a3a", textAlign:"center", marginTop:5 }}>
                  Generá desde el Cotizador para poder guardar
                </div>
              )}
            </div>

          </div>

          {waText && (
            <div className="gen-card gen-wa-preview">
              <div className="gen-wa-titulo">Preview WhatsApp</div>
              <pre className="gen-wa-text">{waText}</pre>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}